import React, { useEffect, useRef, useState } from "react";
import { Table as ReactstrapTable } from "reactstrap";
import { compose, withStateHandlers } from "recompose";
import { withTable } from "components/common";
import { withXeroErrors, withDocumentRenderer } from "components";
import { Trans, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format_reference, formatCurrency, formatDate } from "services/util";
import Search from "./CrSearch";
import { BulkDraftActions } from "../components/BulkDraftActions";
import { BillCreditNoteDocument, InvoiceCreditNoteDocument } from "components/documents";
import { useOrganisationContext } from "hooks";
import { BulkVoidActions } from "../components/BulkVoidActions";
import { TruncateTd } from "components/common/Tables";
import { ReportingStatus } from "./TableInvoices";

const EmailCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `creditnote_${item.CreditNoteNumber}.pdf`
    ),
  type: "CREDIT_NOTE",
})(({ EmailDocument, DocumentRenderer, i, componentDoc, type, name, selectedOrganisation }) => (
  <>
    <EmailDocument compact item={i} type="CREDIT_NOTE" />
    <DocumentRenderer component={componentDoc} type={type} name={name} selectedOrganisation={selectedOrganisation} />
  </>
));

const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `creditnote_${item.CreditNoteNumber}.pdf`
    ),
  type: "CREDIT_NOTE",
})(({ DownloadDocument, DocumentRenderer, i, componentDoc, type, name, selectedOrganisation }) => (
  <>
    <DownloadDocument compact item={i} className="mr-1" />
    <DocumentRenderer component={componentDoc} type={type} name={name} selectedOrganisation={selectedOrganisation} />
  </>
));

const getDocumentName = (i) => {
  return i.DocumentType === 'standard' ? 'Taxed Credit Note' : 'Simplified Credit Note';
};

export const CreditNoteTable = ({
  XeroErrors,
  Pagination,
  TableContainer,
  ViewingRecords,
  Loader,
  XeroTableDataFetch,
  data,
  setFilters,
  status,
  payable,
  t,
  filters,
  refreshData,
  selected,
  setData,
}) => {

  const { selectedOrganisation } = useOrganisationContext()

  const initialFilters = useRef(filters);

  const accPayCheck = /purchases|payable|accounts_payable/g

  const isPayable = accPayCheck.test(window.location.href);

  const baseURL = `/${isPayable ? "purchases" : "accounts_receivable"}/credit_notes`

  const [selectedDocType, setSelectedDocType] = useState(null);

  const selectedList = Object.keys(selected).filter(key => selected[key]);

  const selectedInvoiceList = data?.results?.filter(item => selectedList.includes(item.CreditNoteID))

  useEffect(() => {
    if (data?.results.length) {
      const _data = data?.results.map(
        (item) => ({
          ...item,
          Reference: format_reference(item.Reference),
          DocumentType: item.DocumentType || "standard",
        })
      )
      setData({ ...data, results: _data })
    }
  }, [data?.results.length])

  return (
    <div className="inv-table">
      <XeroErrors color="warning" />
      <Search
        onFiltersChanged={(v) => {
          setFilters(v === null ? initialFilters.current : v);
        }}
        status={status}
        creditNote
        exportTypeShow={true}
        selectedItems={selectedInvoiceList}
        payable={isPayable}
        isPayable={isPayable}
        selectedOrganisation={selectedOrganisation}
        setSelectedDocType={setSelectedDocType}
      />
      <div className="row my-3">
        <div className="col-6 text-left">
          {status === "DRAFT" && (
            <BulkDraftActions
              onSuccess={refreshData}
              items={data.results}
              url="creditnotes"
              pk="CreditNoteID"
              selected={selected}
            />
          )}
          {status === "AUTHORISED" && (
            <BulkVoidActions
              onSuccess={refreshData}
              items={data.results}
              url="creditnotes"
              pk="CreditNoteID"
              selected={selected}
            />
          )}
          {status === "PAID" && (
            <BulkVoidActions
              onSuccess={refreshData}
              items={data.results}
              url="creditnotes"
              pk="CreditNoteID"
              selected={selected}
            />
          )}
        </div>
      </div>
      <div className="small">
        <TableContainer idKey="CreditNoteID">
          <ReactstrapTable size="sm" className="small" responsive>
            <thead>
              <tr>
                <th select />
                <th sortBy="CreditNoteNumber">
                  <Trans>Number</Trans>
                </th>
                <th sortBy="Reference">
                  <Trans>Ref</Trans>
                </th>
                <th sortBy="Contact.Name">
                  <Trans>{payable ? "From" : "To"}</Trans>
                </th>
                {!isPayable  && <th className="text-center text-nowrap">
                  <Trans>Type</Trans>
                </th>}
                <th sortBy="Status">
                  <Trans>Status</Trans>
                </th>
                <th sortBy="Date">
                  <Trans>Date</Trans>
                </th>
                <th className="text-nowrap" sortBy="Total">
                  <Trans>Total</Trans>
                </th>
                <th className="text-nowrap" sortBy="RemainingCredit">
                  <Trans>Remaining</Trans>
                </th>
                {!isPayable && <th className="text-nowrap">
                  <Trans>Clearance</Trans>
                </th>}
                <th className="text-nowrap">
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.loading && (
                <tr>
                  <td colSpan="9" className="text-center">
                    <span className="fa fa-spinner fa-spin mt-2" /> <Trans>Loading...</Trans>
                  </td>
                </tr>
              )}
              {data.results.filter((i) => selectedDocType? (selectedDocType==='undefined' ? !i.DocumentType : i.DocumentType === selectedDocType) : true).map((i, idx) => (
                <tr key={i.CreditNoteID}>
                  <td select />
                  <TruncateTd content={i.CreditNoteNumber || i.Reference} _key={`number_${idx}`} className="truncate">
                    <Link to={`${baseURL}/view/${i.CreditNoteID}`}>
                      {i.CreditNoteNumber || i.Reference || <Trans>No reference</Trans>}
                    </Link>
                  </TruncateTd>
                  <TruncateTd content={i.Reference} _key={`reference_${idx}`} className="truncate"> {i.Reference || t("-")} </TruncateTd>
                  <TruncateTd content={i.Contact?.Name} _key={`contact_${idx}`} className="truncate">
                    {i.Contact &&
                      <Link to={`/contacts/view/${i.Contact.ContactID}`} >
                        {i.Contact.Name}
                      </Link>
                    }
                  </TruncateTd>
                  {!isPayable && <td className="text-center text-capitalize font-weight-normal">{i.DocumentType || "-"}</td>}
                  <td>{i.Status}</td>
                  <td>{formatDate(i.Date)}</td>
                  <td>{formatCurrency(i.Total)}</td>
                  <td>{formatCurrency(i.RemainingCredit)}</td>
                  {!isPayable && <td>{ReportingStatus(i.Reporting)}</td>}
                  <td>
                    <DownloadCell i={i} componentDoc={isPayable ? BillCreditNoteDocument : InvoiceCreditNoteDocument} name={getDocumentName(i)} selectedOrganisation={selectedOrganisation} />
                    <EmailCell i={i} componentDoc={isPayable ? BillCreditNoteDocument : InvoiceCreditNoteDocument} name={getDocumentName(i)} selectedOrganisation={selectedOrganisation} />
                  </td>
                </tr>
              ))}
            </tbody>
          </ReactstrapTable>
        </TableContainer>
      </div>
      <XeroTableDataFetch />
      {/* <div className="d-flex justify-content-between">
        <Pagination size="sm" count={0} noPages />
        <ViewingRecords noCount />
      </div> */}
      <div className="float-right">
        <Pagination size="sm" noPages />
      </div>
      <div className="clearfix">
        <ViewingRecords noCount className="float-left" />
      </div>
    </div>
  );
};

export const enhance = compose(
  withTranslation(),
  withStateHandlers(({ payable }) => {
    return {
      baseUrl: payable
        ? "/purchases/credit_notes"
        : "/accounts_receivable/credit_notes",
    };
  }),
  withXeroErrors(),
  withTable({
    limit: 50,
    url: "creditnotes",
    ordering: {
      column: "Date",
      direction: 2,
    },
  })
);

export default enhance(CreditNoteTable);
